import {
  useMutation,
  useQueryClient,
  queryOptions,
} from "@tanstack/react-query";
import { authenticatedRequest } from "@/lib/graphql/client";
import {
  ADMIN_CHANGELOG_CATEGORIES,
  ADMIN_CHANGELOG_ENTRIES,
  ADMIN_CHANGELOG_ENTRY_BY_ID,
} from "@/lib/graphql/queries/changelog";
import {
  ADMIN_CREATE_CHANGELOG_CATEGORY,
  ADMIN_UPDATE_CHANGELOG_CATEGORY,
  ADMIN_DELETE_CHANGELOG_CATEGORY,
  ADMIN_CREATE_CHANGELOG_ENTRY,
  ADMIN_UPDATE_CHANGELOG_ENTRY,
  ADMIN_PUBLISH_CHANGELOG_ENTRY,
  ADMIN_UNPUBLISH_CHANGELOG_ENTRY,
  ADMIN_DELETE_CHANGELOG_ENTRY,
  ADMIN_GENERATE_CHANGELOG_DRAFT,
  ADMIN_GENERATE_CHANGELOG_TWEET,
} from "@/lib/graphql/mutations/changelog";
import type {
  ChangelogCategory,
  ChangelogEntriesResult,
  ChangelogEntryDetail,
  ChangelogEntryFilters,
  CreateChangelogCategoryInput,
  UpdateChangelogCategoryInput,
  CreateChangelogEntryInput,
  UpdateChangelogEntryInput,
  GenerateChangelogDraftInput,
  GenerateChangelogDraftResult,
  GenerateChangelogTweetResult,
} from "@/types/changelog";
import type { PaginationInput, MutationResult } from "@/types/common";
import { toast } from "sonner";

export const changelogKeys = {
  all: ["changelog"] as const,
  categories: () => [...changelogKeys.all, "categories"] as const,
  lists: () => [...changelogKeys.all, "list"] as const,
  list: (filters: ChangelogEntryFilters & PaginationInput) =>
    [...changelogKeys.lists(), filters] as const,
  details: () => [...changelogKeys.all, "detail"] as const,
  detail: (id: string) => [...changelogKeys.details(), id] as const,
};

// ===== Query Options =====

export function changelogCategoriesQueryOptions() {
  return queryOptions({
    queryKey: changelogKeys.categories(),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminChangelogCategories: ChangelogCategory[];
      }>(ADMIN_CHANGELOG_CATEGORIES);

      return data.adminChangelogCategories;
    },
    staleTime: 60000,
  });
}

export function changelogEntriesQueryOptions(
  filters: ChangelogEntryFilters,
  pagination: PaginationInput,
) {
  return queryOptions({
    queryKey: changelogKeys.list({ ...filters, ...pagination }),
    queryFn: async () => {
      const input = {
        page: pagination.page,
        limit: pagination.limit,
        status: filters.status || undefined,
        categoryId: filters.categoryId || undefined,
      };

      const data = await authenticatedRequest<{
        adminChangelogEntries: ChangelogEntriesResult;
      }>(ADMIN_CHANGELOG_ENTRIES, { input });

      return data.adminChangelogEntries;
    },
    placeholderData: (previousData) => previousData,
    staleTime: 30000,
  });
}

export function changelogEntryDetailQueryOptions(entryId: string) {
  return queryOptions({
    queryKey: changelogKeys.detail(entryId),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminChangelogEntryById: ChangelogEntryDetail;
      }>(ADMIN_CHANGELOG_ENTRY_BY_ID, { entryId });

      return data.adminChangelogEntryById;
    },
    enabled: !!entryId,
  });
}

// ===== Category Mutations =====

export function useCreateChangelogCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateChangelogCategoryInput) => {
      const data = await authenticatedRequest<{
        adminCreateChangelogCategory: ChangelogCategory;
      }>(ADMIN_CREATE_CHANGELOG_CATEGORY, { input });

      return data.adminCreateChangelogCategory;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: changelogKeys.categories(),
      });
      toast.success("카테고리가 생성되었습니다");
    },
    onError: (error: Error) => {
      toast.error("카테고리 생성 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

export function useUpdateChangelogCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateChangelogCategoryInput) => {
      const data = await authenticatedRequest<{
        adminUpdateChangelogCategory: ChangelogCategory;
      }>(ADMIN_UPDATE_CHANGELOG_CATEGORY, { input });

      return data.adminUpdateChangelogCategory;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: changelogKeys.categories(),
      });
      toast.success("카테고리가 수정되었습니다");
    },
    onError: (error: Error) => {
      toast.error("카테고리 수정 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

export function useDeleteChangelogCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (categoryId: string) => {
      const data = await authenticatedRequest<{
        adminDeleteChangelogCategory: MutationResult;
      }>(ADMIN_DELETE_CHANGELOG_CATEGORY, { categoryId });

      return data.adminDeleteChangelogCategory;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: changelogKeys.categories(),
      });
      toast.success("카테고리가 삭제되었습니다");
    },
    onError: (error: Error) => {
      toast.error("카테고리 삭제 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

// ===== Entry Mutations =====

export function useCreateChangelogEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateChangelogEntryInput) => {
      const data = await authenticatedRequest<{
        adminCreateChangelogEntry: ChangelogEntryDetail;
      }>(ADMIN_CREATE_CHANGELOG_ENTRY, { input });

      return data.adminCreateChangelogEntry;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: changelogKeys.lists() });
      toast.success("체인지로그가 생성되었습니다");
    },
    onError: (error: Error) => {
      toast.error("체인지로그 생성 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

export function useUpdateChangelogEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateChangelogEntryInput) => {
      const data = await authenticatedRequest<{
        adminUpdateChangelogEntry: ChangelogEntryDetail;
      }>(ADMIN_UPDATE_CHANGELOG_ENTRY, { input });

      return data.adminUpdateChangelogEntry;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: changelogKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: changelogKeys.detail(variables.entryId),
      });
      toast.success("체인지로그가 수정되었습니다");
    },
    onError: (error: Error) => {
      toast.error("체인지로그 수정 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

export function usePublishChangelogEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entryId: string) => {
      const data = await authenticatedRequest<{
        adminPublishChangelogEntry: MutationResult;
      }>(ADMIN_PUBLISH_CHANGELOG_ENTRY, { entryId });

      return data.adminPublishChangelogEntry;
    },
    onSuccess: (_, entryId) => {
      queryClient.invalidateQueries({ queryKey: changelogKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: changelogKeys.detail(entryId),
      });
      toast.success("체인지로그가 발행되었습니다");
    },
    onError: (error: Error) => {
      toast.error("발행 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

export function useUnpublishChangelogEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entryId: string) => {
      const data = await authenticatedRequest<{
        adminUnpublishChangelogEntry: MutationResult;
      }>(ADMIN_UNPUBLISH_CHANGELOG_ENTRY, { entryId });

      return data.adminUnpublishChangelogEntry;
    },
    onSuccess: (_, entryId) => {
      queryClient.invalidateQueries({ queryKey: changelogKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: changelogKeys.detail(entryId),
      });
      toast.success("체인지로그가 비공개로 전환되었습니다");
    },
    onError: (error: Error) => {
      toast.error("비공개 전환 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

export function useDeleteChangelogEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entryId: string) => {
      const data = await authenticatedRequest<{
        adminDeleteChangelogEntry: MutationResult;
      }>(ADMIN_DELETE_CHANGELOG_ENTRY, { entryId });

      return data.adminDeleteChangelogEntry;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: changelogKeys.lists() });
      toast.success("체인지로그가 삭제되었습니다");
    },
    onError: (error: Error) => {
      toast.error("삭제 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

// ===== AI Draft Generation =====

export function useGenerateChangelogDraft() {
  return useMutation({
    mutationFn: async (input: GenerateChangelogDraftInput) => {
      const data = await authenticatedRequest<{
        adminGenerateChangelogDraft: GenerateChangelogDraftResult;
      }>(ADMIN_GENERATE_CHANGELOG_DRAFT, { input });

      return data.adminGenerateChangelogDraft;
    },
    onError: (error: Error) => {
      toast.error("AI 초안 생성 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

// ===== Tweet Generation =====

export function useGenerateChangelogTweet() {
  return useMutation({
    mutationFn: async (entryId: string) => {
      const data = await authenticatedRequest<{
        adminGenerateChangelogTweet: GenerateChangelogTweetResult;
      }>(ADMIN_GENERATE_CHANGELOG_TWEET, { entryId });

      return data.adminGenerateChangelogTweet;
    },
    onError: (error: Error) => {
      toast.error("트윗 생성 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}
